import React, { useEffect, useMemo } from "react";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import appStyle from "../styles/App.module.css";
import { NavLink, useLocation } from "react-router-dom";
import style from "../styles/Header.module.css";
import { useTheme } from "../contexts/ThemeSelection";
import { useUser } from "../contexts/UserContext";
import { useUserSettings } from "../contexts/UserSettingsContext";
import { ALL_NAV_ITEMS, DEFAULT_NAV_BAR_ITEMS, useAvailableNavItems } from "../hooks/useNavItems";
import useFocusMode from "../hooks/useFocusMode";
import usePregnancy from "../contexts/PregnancyContext";
import HamburgerMenu from "./HamburgerMenu";
import NotificationBell from "./NotificationBell";

const COMMUNITY_MIGRATION_KEY = "onyxlife_community_nav_migrated";

const NavBarComponent: React.FC = () => {
  const userContext = useUser();
  const userDetails = userContext?.userDetails;
  const { activeTheme, theme } = useTheme();
  const linkStyle: React.CSSProperties = { color: theme[activeTheme]?.color };
  const { settings, updateSettings } = useUserSettings();
  const availableItems = useAvailableNavItems();
  const { focusModeEnabled, profile } = useFocusMode();
  const { isPregnant } = usePregnancy();
  const location = useLocation();

  // One-time migration: add community to existing users' nav bar
  useEffect(() => {
    if (!settings.navBarItems) return;
    if (localStorage.getItem(COMMUNITY_MIGRATION_KEY)) return;
    if (settings.navBarItems.includes("community")) {
      localStorage.setItem(COMMUNITY_MIGRATION_KEY, "1");
      return;
    }
    updateSettings({ navBarItems: [...settings.navBarItems, "community"] });
    localStorage.setItem(COMMUNITY_MIGRATION_KEY, "1");
  }, [settings.navBarItems, updateSettings]);

  const navBarItemIds = settings.navBarItems ?? DEFAULT_NAV_BAR_ITEMS;

  // Override labels when in pregnancy mode
  const getNavLabel = (id: string, label: string) => {
    if (!isPregnant) return label;
    if (id === "womens-health") return "Baby";
    if (id === "womens__cycle") return "Pregnancy";
    return label;
  };

  const getNavIcon = (id: string, icon: string) => {
    if (!isPregnant) return icon;
    if (id === "womens-health") return "fa-solid fa-baby";
    if (id === "womens__cycle") return "fa-solid fa-baby";
    return icon;
  };

  // Standard nav items (when focus mode is off).
  // If the user has explicitly saved navBarItems (via onboarding or account settings),
  // trust those even if useAvailableNavItems hasn't re-computed yet — this prevents
  // a race condition where onboarding saves nav items but the UserContext state
  // hasn't propagated, causing the filter to strip out the user's chosen features.
  const hasCustomNav = !!(settings.navBarItems && settings.navBarItems.length > 0);
  const visibleNavItems = useMemo(() => {
    return navBarItemIds
      .filter((id) => hasCustomNav || availableItems.has(id))
      .map((id) => ALL_NAV_ITEMS.find((item) => item.id === id))
      .filter(Boolean) as typeof ALL_NAV_ITEMS;
  }, [navBarItemIds, availableItems, hasCustomNav]);

  // In focus mode, check if a tab nav item matches the current location
  const isTabActive = (path: string): boolean => {
    const [pathname, search] = path.split("?");
    if (location.pathname !== pathname) return false;
    if (!search) return true; // No query params (e.g. "/" or "/notes/") — just match pathname
    const params = new URLSearchParams(search);
    const tabParam = params.get("tab");
    if (!tabParam) return true;
    const currentParams = new URLSearchParams(location.search);
    const currentTab = currentParams.get("tab");
    // If no tab in URL, match the first tab for this same pathname
    if (!currentTab) {
      const samePageTabs = profile?.tabNav.filter(t => t.path.split("?")[0] === pathname) || [];
      if (samePageTabs.length > 0) {
        const firstTabParams = new URLSearchParams(samePageTabs[0].path.split("?")[1] || "");
        return tabParam === firstTabParams.get("tab");
      }
    }
    return currentTab === tabParam;
  };

  const accentColor = theme[activeTheme].todayColor || theme[activeTheme].altColor || theme[activeTheme].secondaryColor || theme[activeTheme].color;

  const navigationBar = (
    <Container
      fluid
      className={`${style.FixedNavbar}`}
      style={{
        backgroundColor: theme[activeTheme].panelColor,
        color: theme[activeTheme].color,
      }}
    >
      <div className={appStyle.NavRow}>
        {userDetails?.user && (
          <div className={appStyle.NavButtons}>
            <HamburgerMenu iconSize="18px" />
          </div>
        )}

        {userDetails?.user && focusModeEnabled && profile ? (
          <>
            {/* Focus mode: render the focused page's tabs as nav items */}
            {profile.tabNav.map((tab) => {
              const active = isTabActive(tab.path);
              return (
                <Nav.Link
                  key={tab.id}
                  to={tab.path}
                  style={active ? { color: accentColor } : linkStyle}
                  as={NavLink}
                  className={appStyle.NavButtons}
                >
                  <i className={getNavIcon(tab.id, tab.icon)} />
                  <span>{getNavLabel(tab.id, tab.label)}</span>
                </Nav.Link>
              );
            })}
            <Nav.Link
              to="/community"
              style={location.pathname === "/community" ? { color: accentColor } : linkStyle}
              as={NavLink}
              className={appStyle.NavButtons}
            >
              <i className="fa-solid fa-users" />
              <span>Community</span>
            </Nav.Link>
            <div className={`${appStyle.NavButtons} ${appStyle.DesktopOnly}`}>
              <NotificationBell />
            </div>
          </>
        ) : (
          <>
            {/* Standard mode: home + configured nav items */}
            <Nav.Link
              to="/"
              style={linkStyle}
              as={NavLink}
              className={appStyle.NavButtons}
            >
              <i className="fa-solid fa-house" />
              <span>Today</span>
            </Nav.Link>
            {userDetails?.user ? (
              <>
                {visibleNavItems.map((item) => (
                  <Nav.Link
                    key={item.id}
                    to={item.path}
                    style={linkStyle}
                    as={NavLink}
                    className={appStyle.NavButtons}
                  >
                    <i className={getNavIcon(item.id, item.icon)} />
                    <span>{getNavLabel(item.id, item.label)}</span>
                  </Nav.Link>
                ))}
                <div className={`${appStyle.NavButtons} ${appStyle.DesktopOnly}`}>
                  <NotificationBell />
                </div>
              </>
            ) : (
              <>
                <Nav.Link
                  to="/sign-up"
                  style={linkStyle}
                  as={NavLink}
                  className={appStyle.NavButtons}
                >
                  Sign up
                </Nav.Link>
                <Nav.Link
                  to={"/sign-in"}
                  style={linkStyle}
                  as={NavLink}
                  className={appStyle.NavButtons}
                >
                  Sign in
                </Nav.Link>
              </>
            )}
          </>
        )}
      </div>
    </Container>
  );

  return <>{navigationBar}</>;
};

export default NavBarComponent;
